import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { getService } from '@Client/runner.hooks/useService';
import {
  GridColumn as Column,
  GridCustomCellProps,
  GridNoRecords
} from '@progress/kendo-react-grid';
import {
  CompositeFilterDescriptor,
  State
} from '@progress/kendo-data-query';
import KendoReactServerGrid, {
  GridCustomFilterCellProps,
  GridDataStateChangeEvent
} from '@Shared.Angular/kendo.react/KendoReactServerGrid';
import GridAutoCompleteFilter from '@Shared.Angular/flowingly.components/Filters/AutoCompleteFilter/GridAutoCompleteFilter';
import GridTextFilter from '@Shared.Angular/flowingly.components/Filters/GridTextFilter';
import { useRemoteDataSource } from '@progress/kendo-react-data-tools';
import { Guid } from '@Shared.Angular/@types/guid';
import { MaintenanceUserListActions } from './MaintenanceUserListActions';
import { IUserSummary } from '@Shared.Angular/@types/core/contracts/queryModel/user/userSummary';

type Props = {
  selectedBusinessId: Guid | null;
  onGridNeedResize?: (needsResize: boolean) => void;
};

export const MaintenanceUsersList = ({ selectedBusinessId, onGridNeedResize }: Props) => {
  const [filter, setFilter] = useState<CompositeFilterDescriptor>(null);
  const dataState = useRef<State>({});
  const [total, setTotal] = useState(0);
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);
  const maintenanceService = getService('maintenanceService');
  const APP_CONFIG = getService('APP_CONFIG');

  const dataSource = useRemoteDataSource<IUserSummary>({
    transport: {
      read: (options): Promise<IUserSummary[]> => {
        const apiOptions = { ...options, pageSize: options.take };
        const usersPromise = new Promise<IUserSummary[]>(
          (resolve, reject) => {
            if (selectedBusinessId != null) {
              maintenanceService
                .getUsersWithOptions(apiOptions, selectedBusinessId)
                .then((response) => {
                  setTotal(response.total);
                  setInitialDataLoaded(true);
                  onGridNeedResize?.(true);
                  resolve(response.users);
                })
                .catch(() => {
                  reject('Error loading users');
                });
            } else {
              setTotal(0);
              resolve([]);
            }
          }
        );
        return usersPromise;
      }
    },
    defaultTake: APP_CONFIG.gridReportPageSize,
    serverPaging: true,
    serverSorting: true,
    serverFiltering: true,
    schema: {
      model: {
        id: 'id'
      }
    }
  });

  useEffect(() => {
    dataState.current = dataSource.dataState;
    dataSource.read();
  }, [selectedBusinessId]);

  const handleUserUpdated = useCallback(() => {
    dataSource.setDataState(dataState.current);
    dataSource.read();
    onGridNeedResize?.(true);
  }, [dataSource, onGridNeedResize]);

  const IdFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridTextFilter(props, 'Search by Id', undefined, 250),
    []
  );

  const NameFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridTextFilter(props, 'Search by Name', undefined, 250),
    []
  );

  const EmailFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridTextFilter(props, 'Search by Email', undefined, 250),
    []
  );

  const ManagerFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridTextFilter(props, 'Search by Manager', undefined, 250),
    []
  );

  const InviteUserFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, ['true', 'false'], 'Search by Invite User'),
    []
  );

  const DeleteFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, ['true', 'false'], 'Search by Delete'),
    []
  );

  const LoginPossibleFilterCellMemo = useMemo(
    () => (props: GridCustomFilterCellProps) =>
      GridAutoCompleteFilter(props, ['Yes', 'No'], 'Search by Login Possible'),
    []
  );

  const generateActionsCell = (props: GridCustomCellProps) => {
    const user: IUserSummary = props.dataItem;
    return (
      <td>
        <MaintenanceUserListActions
          user={user}
          onUserUpdated={handleUserUpdated}
        />
      </td>
    );
  };

  const handleDataStateChange = (event: GridDataStateChangeEvent) => {
    dataSource.setDataState(event.dataState);
    dataSource.read();
  };

  const initialSort = [{ field: 'fullName', dir: 'asc' as 'asc' | 'desc' }];

  return (
    <div className="maintenance-users-container ml-15 mr-30 mt-10 p-0">
      <KendoReactServerGrid
        id="maintenance-users-grid"
        data={dataSource.data}
        dataState={dataSource.dataState}
        onDataStateChange={handleDataStateChange}
        dataItemKey="id"
        defaultSort={initialSort}
        filter={filter}
        filterable={true}
        onFilterChange={(a) => {
          setFilter(a.filter);
        }}
        pageable={{
          buttonCount: 10,
          info: true,
          previousNext: true
        }}
        sortable={true}
        total={total}
      >
        <Column
          field="id"
          title="Id"
          width="300px"
          className="column-id"
          cells={{ filterCell: IdFilterCellMemo }}
        />
        <Column
          field="fullName"
          title="Name"
          className="column-name"
          cells={{ filterCell: NameFilterCellMemo }}
        />
        <Column
          field="email"
          title="Email"
          className="column-email"
          cells={{ filterCell: EmailFilterCellMemo }}
        />
        <Column
          field="managerName"
          title="Manager"
          className="column-manager"
          cells={{ filterCell: ManagerFilterCellMemo }}
        />
        <Column
          field="inviteUser"
          title="Invite User"
          width="150px"
          className="column-invite-user"
          cells={{ filterCell: InviteUserFilterCellMemo }}
        />
        <Column
          field="delete"
          title="Deleted"
          width="150px"
          className="column-delete"
          cells={{ filterCell: DeleteFilterCellMemo }}
        />
        <Column
          field="loginPossible"
          title="Login Possible"
          width="150px"
          className="column-login-possible"
          cells={{ filterCell: LoginPossibleFilterCellMemo }}
        />
        <Column
          title="Actions"
          width="100px"
          filterable={false}
          cells={{ data: generateActionsCell }}
        />
        <GridNoRecords>
          {initialDataLoaded ? 'No Users found' : 'Loading...'}
        </GridNoRecords>
      </KendoReactServerGrid>
    </div>
  );
};
